import { Edit, useForm, useSelect } from "@refinedev/antd";
import { useGo } from "@refinedev/core";
import {
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Button,
  Row,
  Col,
  Card,
  App,
  Breadcrumb,
  Spin,
  Upload,
} from "antd";
import {
  EnvironmentOutlined,
  HomeOutlined,
  AuditOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { useParams, Link } from "react-router";
import { auditPlotService } from "../../services/api";
import { AdjacentCropMapEditor } from "./components/AdjacentCropMapEditor";
import "./adjacent-crops.css";
import { usePageTitle } from "../../hooks";

const { TextArea } = Input;
const { Dragger } = Upload;

interface PhotoItem {
  uid: string;
  file: File;
  description: string;
  preview: string;
}

/**
 * AdjacentCropEdit Component
 * Form for editing an existing adjacent crop
 * Only allows editing the first review data
 */
export const AdjacentCropEdit = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const go = useGo();
  const { id } = useParams<{ id: string }>();
  
  // Set page title
  usePageTitle(t("adjacent_crops.edit.title", "Editar Cultivo Adyacente"));

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [geometry, setGeometry] = useState<GeoJSON.Geometry | null>(null);
  const [referencePlots, setReferencePlots] = useState<GeoJSON.FeatureCollection | null>(null);
  const [controlPoints, setControlPoints] = useState<GeoJSON.FeatureCollection | null>(null);
  const [adjacentCrop, setAdjacentCrop] = useState<any>(null);
  const [auditId, setAuditId] = useState<number | null>(null);
  const [newPhotos, setNewPhotos] = useState<PhotoItem[]>([]);

  const { formProps, form } = useForm({
    action: "edit",
    redirect: false,
    id: id,
  });

  // Load species for select dropdown
  const { selectProps: speciesSelectProps } = useSelect({
    resource: "crop-species",
    optionLabel: "name",
    optionValue: "id",
    filters: [
      {
        field: "is_active",
        operator: "eq",
        value: true,
      },
    ],
    pagination: {
      pageSize: 500,
    },
    sorters: [
      {
        field: "name",
        order: "asc",
      },
    ],
    queryOptions: {
      enabled: !isLoadingData,
    },
  });

  // Load varieties based on selected species
  const selectedSpecies = Form.useWatch("species", form);
  const { selectProps: varietySelectProps } = useSelect({
    resource: "crop-varieties",
    optionLabel: "name",
    optionValue: "id",
    filters: [
      {
        field: "species",
        operator: "eq",
        value: selectedSpecies,
      },
      {
        field: "is_active",
        operator: "eq",
        value: true,
      },
    ],
    pagination: {
      pageSize: 500,
    },
    sorters: [
      {
        field: "name",
        order: "asc",
      },
    ],
    queryOptions: {
      enabled: !!selectedSpecies,
    },
  });

  // Load adjacent crop data
  useEffect(() => {
    if (!id) {
      message.error("ID de cultivo adyacente no especificado");
      go({ to: "/adjacent-crops" });
      return;
    }

    loadAdjacentCropData();
  }, [id]);

  // Photo handlers
  const handlePhotoChange = (info: any) => {
    const { fileList } = info;
    
    const photos: PhotoItem[] = fileList.map((file: UploadFile) => {
      if (file.originFileObj) {
        return {
          uid: file.uid,
          file: file.originFileObj as File,
          description: "",
          preview: URL.createObjectURL(file.originFileObj as File),
        };
      }
      return null;
    }).filter(Boolean);

    setNewPhotos(photos);
  };

  const handlePhotoDescriptionChange = (uid: string, description: string) => {
    setNewPhotos((prev) =>
      prev.map((photo) =>
        photo.uid === uid ? { ...photo, description } : photo
      )
    );
  };

  const handleRemoveNewPhoto = (uid: string) => {
    setNewPhotos((prev) => {
      const photo = prev.find(p => p.uid === uid);
      if (photo) {
        URL.revokeObjectURL(photo.preview);
      }
      return prev.filter((photo) => photo.uid !== uid);
    });
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  const loadAdjacentCropData = async () => {
    console.log('🚀 Starting loadAdjacentCropData for id:', id);
    setIsLoadingData(true);
    try {
      const { apiClient } = await import("../../services/api/apiClient");
      console.log('✅ apiClient imported');
      
      // Load adjacent crop detail
      console.log('� AFetching adjacent crop from API...');
      const cropResponse = await apiClient.get(`/adjacent-crops/${id}/`);
      console.log('📡 API Response:', cropResponse);
      const crop = cropResponse.data;
      console.log('📍 Adjacent crop loaded:', crop);
      setAdjacentCrop(crop);

      // Set geometry - try both 'geom' and 'geometry' fields
      const geomData = crop.geom || crop.geometry;
      console.log('📍 Geometry data:', geomData);
      if (geomData) {
        setGeometry(geomData);
        console.log('✅ Geometry set in state:', geomData);
      } else {
        console.log('⚠️ No geometry found in crop data');
      }

      // Get first review data
      const firstReview = crop.reviews?.find((r: any) => r.is_first_review);
      console.log('📍 First review:', firstReview);
      
      if (firstReview) {
        setAuditId(firstReview.audit);
        
        // Set form values with a small delay to ensure form is ready
        setTimeout(() => {
          const formValues = {
            species: crop.species,
            variety: crop.variety || undefined,
            estimated_surface: firstReview.estimated_surface || 0,
            is_producer_owner: crop.is_producer_owner,
            comment: firstReview.comment,
          };
          console.log('📝 Setting form values:', formValues);
          form.setFieldsValue(formValues);
          console.log('✅ Form values set');
        }, 100);

        // Load plots and control points for the audit
        try {
          const plotsResponse = await auditPlotService.getPlotsByAudit(firstReview.audit);
          
          if (plotsResponse && typeof plotsResponse === 'object' && 'results' in plotsResponse) {
            const results = (plotsResponse as any).results;
            
            if (results && typeof results === 'object' && results.type === 'FeatureCollection' && 'features' in results) {
              const validFeatures = (results.features as any[])
                .filter((feature: any) => feature.geometry !== null)
                .map((feature: any) => ({
                  type: "Feature" as const,
                  geometry: feature.geometry,
                  properties: {
                    name: feature.properties?.name || `Parcela ${feature.id}`,
                    surface: feature.properties?.surface,
                  },
                }));
              
              if (validFeatures.length > 0) {
                setReferencePlots({
                  type: "FeatureCollection",
                  features: validFeatures,
                });
              }
            }
          }
        } catch (error) {
          console.error("Error loading plots:", error);
        }

        // Load control points
        try {
          const cpResponse = await apiClient.get(
            `/audit-control-points/open-for-farm/?farm_id=${crop.farm}&audit_id=${firstReview.audit}`
          );
          
          const controlPointsData = cpResponse.data.control_points;
          
          if (controlPointsData && controlPointsData.type === 'FeatureCollection') {
            setControlPoints(controlPointsData);
          }
        } catch (error) {
          console.error("Error loading control points:", error);
        }
      }
    } catch (error: any) {
      console.error("❌ Error loading adjacent crop:", error);
      console.error("❌ Error details:", error.response?.data, error.message);
      message.error("Error al cargar el cultivo adyacente");
      go({ to: "/adjacent-crops" });
    } finally {
      console.log('🏁 loadAdjacentCropData finished');
      setIsLoadingData(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (values: any) => {
    if (!id) {
      message.error("ID de cultivo adyacente no especificado");
      return;
    }

    setIsSubmitting(true);

    try {
      const { apiClient } = await import("../../services/api/apiClient");
      
      // Update adjacent crop basic data
      await apiClient.patch(`/adjacent-crops/${id}/`, {
        species: values.species,
        variety: values.variety || null,
        is_producer_owner: values.is_producer_owner,
        geom: geometry,
      });

      // Update first review
      const firstReview = adjacentCrop.reviews?.find((r: any) => r.is_first_review);
      if (firstReview) {
        await apiClient.patch(`/adjacent-crop-reviews/${firstReview.id}/`, {
          comment: values.comment,
          estimated_surface: values.estimated_surface || 0,
          species: values.species,
          variety: values.variety || null,
        });
      }

      // Upload new photos if any
      if (newPhotos.length > 0) {
        try {
          const photosBase64 = await Promise.all(
            newPhotos.map(async (photo) => ({
              image_base64: await fileToBase64(photo.file),
              description: photo.description,
            }))
          );

          // Upload photos to the first review
          for (const photoData of photosBase64) {
            await apiClient.post(`/plot-photos/`, {
              audit: auditId,
              adjacent_crop_review: firstReview.id,
              ...photoData,
            });
          }

          // Clean up preview URLs
          newPhotos.forEach((photo) => {
            URL.revokeObjectURL(photo.preview);
          });
        } catch (error) {
          console.error("Error uploading photos:", error);
          message.warning("El cultivo se actualizó pero hubo un error al subir las fotos");
        }
      }

      message.success("Cultivo adyacente actualizado exitosamente");

      // Redirect back to audit if we have auditId
      if (auditId) {
        go({ to: `/audits/${auditId}` });
      } else {
        go({ to: "/adjacent-crops" });
      }
    } catch (error: any) {
      console.error("Error updating adjacent crop:", error);
      message.error(
        error.message || "Error al actualizar el cultivo adyacente"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingData) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" tip="Cargando datos..." />
      </div>
    );
  }

  return (
    <Edit
      isLoading={isSubmitting}
      title={`Editar Cultivo Adyacente: ${adjacentCrop?.name || ''}`}
      breadcrumb={
        <Breadcrumb
          items={[
            {
              title: <Link to="/"><HomeOutlined /> {t("home.title", "Inicio")}</Link>,
            },
            {
              title: <Link to="/audits"><AuditOutlined /> {t("audit.title", "Auditorías")}</Link>,
            },
            ...(auditId ? [{
              title: <Link to={`/audits/${auditId}`}>Auditoría #{auditId}</Link>,
            }] : []),
            {
              title: "Editar Cultivo Adyacente",
            },
          ]}
        />
      }
      headerButtons={() => (
        <Button onClick={() => auditId ? go({ to: `/audits/${auditId}` }) : go({ to: "/adjacent-crops" })}>
          {t("common.back", "Volver")}
        </Button>
      )}
      footerButtons={() => (
        <>
          <Button onClick={() => auditId ? go({ to: `/audits/${auditId}` }) : go({ to: "/adjacent-crops" })}>
            {t("common.cancel", "Cancelar")}
          </Button>
          <Button
            type="primary"
            onClick={() => form.submit()}
            loading={isSubmitting}
            icon={<EnvironmentOutlined />}
          >
            {t("common.save", "Guardar")}
          </Button>
        </>
      )}
    >
      <Form {...formProps} layout="vertical" onFinish={handleSubmit}>
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            {/* Basic Information Card */}
            <Card title={t("adjacent_crops.create.basicInfo", "Información Básica")}>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={t("adjacent_crops.field.species", "Especie")}
                    name="species"
                    rules={[
                      {
                        required: true,
                        message: t("adjacent_crops.validation.speciesRequired", "La especie es obligatoria"),
                      },
                    ]}
                  >
                    <Select
                      {...speciesSelectProps}
                      placeholder={t("adjacent_crops.placeholder.species", "Seleccione una especie")}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label={t("adjacent_crops.field.variety", "Variedad")}
                    name="variety"
                  >
                    <Select
                      {...varietySelectProps}
                      placeholder={t("adjacent_crops.placeholder.variety", "Seleccione una variedad")}
                      disabled={!selectedSpecies}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                      }
                      allowClear
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label={t("adjacent_crops.field.estimatedSurface", "Superficie Estimada (ha)")}
                    name="estimated_surface"
                  >
                    <InputNumber
                      min={0}
                      step={0.1}
                      precision={2}
                      style={{ width: "100%" }}
                      placeholder="0.00"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label={t("adjacent_crops.field.isProducerOwner", "¿Es propiedad del productor?")}
                    name="is_producer_owner"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>

                <Col xs={24}>
                  <Form.Item
                    label={t("adjacent_crops.field.comment", "Comentario")}
                    name="comment"
                    rules={[
                      {
                        required: true,
                        message: t("adjacent_crops.validation.commentRequired", "El comentario es obligatorio"),
                      },
                    ]}
                  >
                    <TextArea
                      rows={4}
                      placeholder={t("adjacent_crops.placeholder.comment", "Describa el cultivo adyacente detectado...")}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* Map Card */}
            <Card 
              title={t("adjacent_crops.create.location", "Ubicación en el Mapa")}
              style={{ marginTop: 16 }}
            >
              <AdjacentCropMapEditor
                geometry={geometry}
                onGeometryChange={setGeometry}
                referencePlots={referencePlots}
                controlPoints={controlPoints}
              />
            </Card>

            {/* Photos Card */}
            <Card
              title="Fotografías"
              style={{ marginTop: 16 }}
            >
              <p style={{ marginBottom: 16, color: "#666" }}>
                Añada fotografías del cultivo adyacente.
              </p>

              {/* Upload New Photos */}
              <div>
                <Dragger
                  multiple
                  accept="image/*"
                  beforeUpload={() => false}
                  onChange={handlePhotoChange}
                  fileList={newPhotos.map((p) => ({
                    uid: p.uid,
                    name: p.file.name,
                    status: "done" as const,
                  }))}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    Haga clic o arrastre fotos aquí
                  </p>
                </Dragger>

                {newPhotos.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    {newPhotos.map((photo) => (
                      <Card
                        key={photo.uid}
                        size="small"
                        style={{ marginBottom: 8 }}
                        extra={
                          <Button
                            type="link"
                            danger
                            size="small"
                            onClick={() => handleRemoveNewPhoto(photo.uid)}
                          >
                            Eliminar
                          </Button>
                        }
                      >
                        <Row gutter={16}>
                          <Col span={8}>
                            <img
                              src={photo.preview}
                              alt="Preview"
                              style={{ width: "100%", height: "auto" }}
                            />
                          </Col>
                          <Col span={16}>
                            <Input
                              placeholder="Descripción de la foto"
                              value={photo.description}
                              onChange={(e) =>
                                handlePhotoDescriptionChange(photo.uid, e.target.value)
                              }
                            />
                          </Col>
                        </Row>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          </Col>
        </Row>
      </Form>
    </Edit>
  );
};
